import type { UpdateEventParams } from "@/App/components/Dispatch/DispatchEvents/dispatchEvent";
import type { UserToInvite } from "@/App/components/InviteUsersDropdown/UserToInvite";
import type { PossiblePayloads } from "@/App/composables/types/PossibleCasePayloads";
import config from "@/App/config/main";
import { getBlob, HttpClient } from "@/App/services/httpClient/HttpClient";
import storage from "@/App/services/storage.service";
import type { ChatMessage } from "@/App/types/ChatMessage";
import type { Ift } from "@/App/types/Ift";
import {
  type Address,
  type CaseExtras,
  type Location,
  type RequestBody,
  type TwiageCase,
  type UpdateIFTDatesParams,
} from "@/App/types/twiageTypes";
import { CaseType, FileType } from "@/App/types/twiageTypes";
import { pickFields } from "@/App/utils/pickFields";
import { removeEmptyArrays } from "@/App/utils/removeEmptyArrays";
import { removeEmptyObjects } from "@/App/utils/removeEmptyObjects";
import type { ActivatedProtocolResponse } from "@/Pages/Cases/ActivatedProtocolResponse";
import { CaseListFields } from "@/Pages/Cases/caseListFields";
import type { CaseFormUIFields } from "@/Pages/Cases/components/Forms/common/CaseFormUIFields";
import type { CaseRequestBody } from "@/Pages/Cases/components/Forms/common/CaseRequestBody";
import { caseRequestBodyFields } from "@/Pages/Cases/components/Forms/common/CaseRequestBody";
import type { IftRequestBody } from "@/Pages/Cases/components/Forms/Ift/IftRequestBody";
import type { MihFormPayload } from "@/Pages/Cases/components/Forms/Mih/stores/types/mih";
import { populateCase } from "@/Pages/Cases/functions/case_functions";
import { openPrintCasePDFDialog } from "@/Pages/Cases/functions/openPrintCasePDFDialog";
import { removeUndefinedFields } from "@/Pages/utils/removeUndefinedFields";
import type { EDFeedback, EDFeedbackResponse } from "./components/CaseDetail/HandoffFeedback/EDFeedback";
import type { IftFormPayload } from "./components/Forms/Ift/IftFormPayload";
import type { IftRoom } from "./types/IftRoom";
import { getTimeZoneOffset } from "./functions/getTimeZoneOffset";
import { DynamicFieldType } from "@/Pages/Cases/components/Forms/fields/DynamicFormFields/dynamicFieldType";

const CaseDetailsField = `${CaseListFields},messages,events{clientCreatedAt,createdAt,type,data,organizationName,createdBy{displayName,id}},supportedProtocols,idScanned`;

const UnseenCaseDataKey = "unseenCaseData";

const queryParamsForCasesListAndDetails = {
  includeUnknownComplaint: true,
  doNotCountNonCountableMessages: true,
  addIsAcknowledged: true,
  populateMihEmsCandidates: true,
  mediaTypes: "all",
};

const parseLocation = (
  location: Location,
): {
  hospital?: string;
  address?: Address;
} => {
  return location?.hospital?.id
    ? { hospital: location?.hospital?.id }
    : location?.address
      ? { hospital: undefined, address: location?.address }
      : {};
};

const createCaseRequestBody = (payload: CaseFormUIFields | IftFormPayload | MihFormPayload): CaseRequestBody => {
  const fields = pickFields(payload, ...caseRequestBodyFields) as CaseRequestBody;

  if (payload.dataEnabledTags && payload.id) {
    const dataEnabledTagIds = new Set(payload.dataEnabledTags.map((tag) => tag.id));
    fields.tags = payload.tags.filter((tag) => !dataEnabledTagIds.has(tag));
  } else {
    fields.tags = payload.tags;
  }
  fields.dynamicValues = fields.dynamicValues?.map((dynamicValue) => ({
    ...dynamicValue,
    fields: dynamicValue.fields.filter(
      (field) =>
        field.type !== DynamicFieldType.fileUpload ||
        payload.attachmentsToLeave?.some(({ value }) => value === field.value),
    ),
  }));

  return fields;
};
const convertIftToApiConsumableFormat = (ift: Ift): IftRequestBody => {
  const { approximateTravelDistance, dropOffRoom, expectedPickupTime, pickupRoom } = ift;

  const biddingEmsParticipants = ift.biddingEmsParticipants?.map((participant) => participant.emsAgency.id);
  const biddingHospitalParticipants = ift.biddingHospitalParticipants?.map((participant) => participant.hospital.id);
  const destination = parseLocation(ift.destination);
  const source = parseLocation(ift.source);

  return removeEmptyObjects(
    removeEmptyArrays({
      approximateTravelDistance,
      biddingEmsParticipants,
      biddingHospitalParticipants,
      destination,
      dropOffRoom,
      expectedPickupTime,
      pickupRoom,
      source,
    }),
  );
};

export const CasesService = {
  createCase({ payload }: { payload: CaseFormUIFields | PossiblePayloads }): Promise<TwiageCase> {
    if (!payload.age) {
      payload.agePeriod = null;
    }

    if (payload.type === CaseType.ift) {
      if (!("ift" in payload) || !payload.ift) {
        throw Error("cant create ift case without ift");
      }
      const { ift } = payload;

      return HttpClient.post<TwiageCase>({
        baseUrl: config.API_URL,
        path: "/ift",
        body: { ...payload, ...{ ift: convertIftToApiConsumableFormat(ift) } },
        query: { populateComplaint: true },
      });
    } else {
      return HttpClient.post({ path: "/twiageCases", query: { populateComplaint: true }, body: payload });
    }
  },

  editCase({ caseId, payload }: { caseId: string; payload: CaseFormUIFields | PossiblePayloads }): Promise<TwiageCase> {
    const requestBody: RequestBody = createCaseRequestBody(payload);
    if (payload.ift) {
      requestBody.ift = convertIftToApiConsumableFormat(payload.ift);
    }
    return HttpClient.patch({ path: `/twiageCases/${caseId}`, body: requestBody });
  },

  getCases({
    types,
    dateRangeOptions,
  }: {
    types: CaseType[];
    dateRangeOptions: Record<string, string | undefined>;
  }): Promise<TwiageCase[]> {
    return HttpClient.get<TwiageCase[]>({
      path: "/twiageCases",
      params: {
        ...queryParamsForCasesListAndDetails,
        fields: `{${CaseListFields}}`,
        ...removeUndefinedFields({ ...dateRangeOptions }),
        type: types?.join(",") ?? undefined,
      },
    }).then((cases) => cases.map(populateCase));
  },

  getCaseById({ caseId }: { caseId: string }): Promise<TwiageCase> {
    return HttpClient.get<TwiageCase>({
      path: `twiageCases/${caseId}`,
      params: {
        fields: `{${CaseDetailsField}}`,
        ...queryParamsForCasesListAndDetails,
        includeSupportedProtocols: true,
        rawDataEnabledTags: true,
        skipTagEventsPriorToSetDestination: true,
      },
    }).then(populateCase);
  },

  modifyCase(id: string, payload: RequestBody): Promise<TwiageCase> {
    return HttpClient.patch({ path: `/twiageCases/${id}`, body: payload });
  },

  updateRoom({ caseId, room }: { caseId: string; room?: string }): Promise<TwiageCase> {
    return HttpClient.patch({
      path: `/twiageCases/${caseId}`,
      params: { sendRoomAssignedMessage: true },
      body: { room },
    });
  },

  updateIftRoom({ caseId, iftRoom, room }: { caseId: string; iftRoom: IftRoom; room: string }): Promise<TwiageCase> {
    return HttpClient.patch({
      path: `/twiageCases/${caseId}`,
      body: { ift: { [iftRoom]: room } },
    });
  },

  updateExtras({ caseId, extras }: { caseId: string; extras?: CaseExtras }): Promise<TwiageCase> {
    return HttpClient.put({
      path: `/twiageCases/${caseId}/extras`,
      body: extras,
    });
  },

  downloadFile({
    caseId,
    uploadId,
    fileType,
  }: {
    caseId: string;
    uploadId: string;
    fileType: FileType;
  }): Promise<string> {
    return getBlob({ path: `/twiageCases/${caseId}/uploads/${uploadId}`, fileType });
  },

  caseReport({ caseId }: { caseId: string }): Promise<string> {
    return getBlob({
      path: `/twiageCases/${caseId}/pdf`,
      query: { timezone: getTimeZoneOffset() },
      fileType: FileType.Pdf,
    });
  },

  sendChatMessage({ caseId, message }: { caseId: string; message: string }): Promise<ChatMessage> {
    return HttpClient.post({
      path: `${config.API_URL}/notification/twiage-cases/${caseId}/chat-messages`,
      body: {
        deviceId: "dashboard",
        message,
        source: "hospital_dashboard",
      },
    });
  },

  activateProtocol({ caseId, protocolId }: { caseId: string; protocolId: string }): Promise<ActivatedProtocolResponse> {
    return HttpClient.put({
      path: `${config.API_URL}/protocols/twiage-cases/${caseId}/protocols/${protocolId}`,
    });
  },

  recallProtocol({ caseId, protocolId }: { caseId: string; protocolId: string }): Promise<ActivatedProtocolResponse> {
    return HttpClient.delete({ path: `${config.API_URL}/protocols/twiage-cases/${caseId}/protocols/${protocolId}` });
  },

  activateProtocolStep({
    caseId,
    protocolId,
    stepId,
  }: {
    caseId: string;
    protocolId: string;
    stepId: string;
  }): Promise<ActivatedProtocolResponse> {
    return HttpClient.put({
      path: `${config.API_URL}/protocols/twiage-cases/${caseId}/protocols/${protocolId}/steps/${stepId}`,
    });
  },

  recallProtocolStep({
    caseId,
    protocolId,
    stepId,
  }: {
    caseId: string;
    protocolId: string;
    stepId: string;
  }): Promise<ActivatedProtocolResponse> {
    return HttpClient.delete({
      path: `${config.API_URL}/protocols/twiage-cases/${caseId}/protocols/${protocolId}/steps/${stepId}`,
    });
  },

  getUsersToInvite({ caseId }: { caseId: string }): Promise<Array<UserToInvite>> {
    return HttpClient.get({ path: `/twiageCases/${caseId}/statUsersToInvite` });
  },

  inviteUsers({ caseId, userIds }: { caseId: string; userIds: Array<string> }): Promise<Array<UserToInvite>> {
    return HttpClient.post({ path: `/twiageCases/${caseId}/statUsersToInvite`, body: { userIds } });
  },

  getUnseenCaseData(): Record<string, string[]> {
    const unseenCaseData = storage.getItemAsObject(UnseenCaseDataKey) as Record<string, string[]>;
    return unseenCaseData || {};
  },

  saveUnseenCaseData(unseenCaseData: Record<string, string[]>): void {
    if (Object.keys(unseenCaseData).length) {
      storage.save(UnseenCaseDataKey, unseenCaseData);
    } else {
      storage.removeItem(UnseenCaseDataKey);
    }
  },

  clearUnseenCaseData(): void {
    storage.removeItem(UnseenCaseDataKey);
  },

  updateIFTDates({ caseId, key }: UpdateIFTDatesParams): Promise<TwiageCase> {
    return HttpClient.patch({
      body: {},
      path: `/twiageCases/${caseId}/ift/updateDate/${key}`,
    });
  },

  updateEvent({ caseId, body, mode }: UpdateEventParams): Promise<TwiageCase> {
    return HttpClient.patch({
      body,
      path: `/twiageCases/${caseId}/${mode}/events`,
    });
  },

  async openPrintCasePDFDialog({ caseId }: { caseId: string }): Promise<void> {
    const caseReportPDFObjectURL = await this.caseReport({ caseId });

    openPrintCasePDFDialog({ caseId, caseReportPDFObjectURL });
  },

  createHandoffFeedback({ caseId, feedback }: { caseId: string; feedback: EDFeedback }): Promise<EDFeedbackResponse> {
    return HttpClient.post({ path: `/twiageCases/${caseId}/case-feedback`, body: feedback });
  },
};
